import React, { Suspense, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Paper,
  Grid,
  IconButton,
  Stack,
  Typography,
  Checkbox,
  Box,
  Menu,
  MenuItem,
  Tooltip,
  Badge,
} from "@mui/material";
import { replacedText } from "src/utils";
import {
  CircularLoader,
  LabelPopup,
  PriorityPopover,
  StatusPopover,
  TaskCreatedAt,
  TaskDatePick,
  TeamMembers,
  TeamPicker,
  UserAvatar,
} from "..";
import Iconify from "../Iconify";
import Label from "../Label";
import { useAppContext } from "src/hooks";
import { _project_team } from "src/DAL";
import { useSnackbar } from "notistack";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { string_with_limited_length } from "src/utils/common";
import { capitalCase } from "change-case";

/* <<--------------------------------------------------------->> */

const stack_props = {
  direction: "row",
  alignItems: "center",
  columnGap: 0.5,
};
const icon_color = "#637381";
const menu_item_icon_size = { height: "1rem", width: "1rem" };
/* <<--------------------------------------------------------->> */

const AllTasksVariantUnOptimized = ({
  task,
  taskTeam,
  onOpenComments,
  onTaskAction,
  onSetSelectedTask,
  projects = [],
  getListing,
  onChangeTags,
  completed,
}) => {
  const { dispatch_get_user_profile } = useAppContext();
  const navigate = useNavigate();
  const userProfile = dispatch_get_user_profile();

  const { createdAt, end_date, priority } = task;
  const { task_title, task_action_by, creator_profile, time_track } = task;
  const role = task_action_by;
  const { task_status, team, extra_status, tag_colors } = task;
  const [open, setOpen] = useState(null);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [taskProjectTeam, setTaskProjectTeam] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [projectTags, setProjectTags] = useState(() => {
    if (task?.project_data) {
      return task.project_data.tag_colors;
    } else {
      return projects.find((project) => project._id == task.project.id)
        ?.tag_colors;
    }
  });
  const { tagExpand, setTagExpand } = useAppContext();

  const handleNavProject = (_task) => {
    navigate(`/workspaces/projects/${task.project.id}`, {
      state: {
        scroll_to_task: _task._id,
        expand_section_id: _task.section_id,
      },
    });
  };

  const handleOpen = (e, task) => {
    onSetSelectedTask(task);
    setOpen(e.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleAction = (action, data) => {
    onTaskAction(action, data);
  };

  const handleCopy = (task) => {
    onTaskAction("copy_task_link", task);
  };

  const onStatusCheckChange = (e) => {
    let checked = e.target.checked;
    const data = {
      checked: checked,
      task: task,
      is_checkbox: true,
    };
    handleAction("change_status", data);
  };

  const onStatusChange = (_data) => {
    const data = {
      task: task,
      status: _data.value,
      is_checkbox: false,
    };
    handleAction("change_status", data);
  };

  const onPriorityChange = (_data) => {
    const data = { task: task, priority: _data.value };
    handleAction("change_priority", data);
  };

  const onDateChange = (newDate) => {
    const data = { task: task, endDate: newDate };
    handleAction("change_date", data);
  };

  const onOpenDetail = (_task) => {
    handleAction("open_task_detail", _task);
  };

  const canRemoveTeam = (_targetUser, _task) => {
    let current_user_id = userProfile.user_id;
    let target_user_id = _targetUser.user;
    if (String(role) === "admin" || String(role) === "manager") {
      return true;
    }
    if (String(current_user_id) === String(target_user_id)) {
      return false;
    }
    if (String(role) === "team") {
      let found_index_in_team = undefined;
      _task.team.map((item, index) => {
        if (String(item.user) === String(target_user_id)) {
          found_index_in_team = index;
        }
      });
      if (
        String(current_user_id) ===
        String(_task.team_assign_by[found_index_in_team])
      ) {
        return true;
      }
    }
    return false;
  };

  const onTeamChange = (target_user, task_team, is_selected, action) => {
    const data = {
      task: task,
      taskTeam: task_team,
      targetUser: target_user,
      isSelected: is_selected,
      action: action,
    };
    if (String(data.action) === "remove") {
      if (canRemoveTeam(data.targetUser, data.task) === false) {
        return;
      }
    }
    handleAction("change_members", data);
  };

  const isChecked = () => {
    return String(task_status) === "3" || String(task_status) === "4";
  };

  const allowDateAction = () => {
    if (task.creator_profile.user_id === userProfile.user_id) return true;
    if (task.user_task_status === true) return true;
    if (["admin", "manager", "client", "client_team"].includes(String(role)))
      return true;
    return false;
  };

  const excludedStatusOptions = () => {
    const currentUserRole = task.role;
    if (
      !currentUserRole ||
      creator_profile.user_id === dispatch_get_user_profile().user_id
    )
      return [5];
    if (["admin", "manager", "client", "client_team"].includes(currentUserRole))
      return [5];
    if (currentUserRole === "team") return [4, 5];
    return [];
  };

  const getTaskProjectTeam = async () => {
    const result = await _project_team(null, task.project.id);
    if (result.code === 200) {
      setTaskProjectTeam(result.project_team_list.team);
      setLoadingTeam(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setLoadingTeam(false);
    }
  };

  const handleScroll = () => {
    const elm = document.getElementById(task._id);
    if (elm && task.scroll_to_view === true) {
      elm.scrollIntoView({ block: "center", behavior: "auto" });
      setTimeout(() => elm.classList.add("highlight-task"), 500);
      setTimeout(() => elm.classList.remove("highlight-task"), 3000);
    }
  };

  useEffect(() => {
    handleScroll();
    return () => setTaskProjectTeam([]);
  }, []);

  return (
    <React.Fragment>
      <Paper
        id={task._id}
        onClick={(e) => {
          if (e.target.closest("a")) return;
          onOpenDetail(task);
        }}
        elevation={3}
        sx={{
          width: 1,
          borderRadius: "0.3rem",
          p: 0.7,
          pt: 0.9,
          pb: 0.9,
          position: "relative",
          pr: 7.5,
          overflow: "hidden",
        }}
      >
        <Stack spacing={0.3}>
          {/* Header Row */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ width: 1 }}
          >
            <Checkbox
              sx={{ mb: "auto", p: "4px" }}
              value={task_status}
              onChange={onStatusCheckChange}
              checked={isChecked()}
              onClick={(e) => e.stopPropagation()}
            />
            <Typography
              role="button"
              sx={{ cursor: "pointer", overflow: "hidden" }}
            >
              <div
                dangerouslySetInnerHTML={replacedText(task_title)}
                style={{
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              />
            </Typography>
          </Stack>

          <Grid container>
            <Grid
              item
              flexGrow={1}
              sx={{
                paddingLeft: "9px",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                minWidth: 0,
              }}
            >
              <Grid container direction="row">
                {/* Mobile Scroller (below md) */}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: { xs: "block", md: "none" },
                    overflowX: "auto",
                    width: "100%",
                    "&::-webkit-scrollbar": { height: "3px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: (theme) => theme.palette.divider,
                      borderRadius: "10px",
                    },
                    mt: 1,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2.5}
                    sx={{ width: "max-content", py: 1, pb: 1.5 }}
                  >
                    <TaskDatePick
                      hasPortal
                      value={end_date}
                      onChange={onDateChange}
                      task={task}
                      disabled={!allowDateAction()}
                    />
                    <Stack direction="row" alignItems="center" spacing={0.7}>
                      <Tooltip
                        title={`created by ${creator_profile.first_name} ${creator_profile.last_name}`}
                      >
                        <Box onClick={(e) => e.stopPropagation()}>
                          <UserAvatar
                            sx={{ width: 35, height: 35 }}
                            image={creator_profile.image}
                            name={`${creator_profile.first_name} ${creator_profile.last_name}`}
                          />
                        </Box>
                      </Tooltip>
                      <TaskCreatedAt date={createdAt} />
                      <PriorityPopover
                        onChange={onPriorityChange}
                        allowOpen
                        value={priority}
                      />
                      <StatusPopover
                        onChange={onStatusChange}
                        extraStatus={extra_status}
                        allowOpen
                        excludedOptions={excludedStatusOptions()}
                        value={task_status}
                      />
                      <Badge
                        badgeContent=""
                        color="error"
                        invisible={task.task_comment_unread_count == 0}
                        variant="dot"
                        /// Dont open comment drawer instead open task detail & scroll to comment
                        // onClick(() => onOpenComments(task))
                        onClick={(e) => {
                          onOpenDetail(task);
                          setTimeout(() => {
                            const commentsSection = document.querySelector('[data-comments-section]');
                            if (commentsSection) {
                              commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 300);
                        }}
                        // -------------------------------------------------
                        className="pointer"
                      >
                        <Iconify
                          sx={{ height: 17, width: 17, color: icon_color }}
                          icon="majesticons:comment-text"
                        />
                      </Badge>
                      <Typography variant="body2">
                        {task.task_comment.length}
                      </Typography>
                      <Iconify
                        sx={{ height: 17, width: 17, color: icon_color }}
                        icon="clarity:copy-line"
                        onClick={() => handleCopy(task)}
                        className="pointer"
                      />
                      <LabelPopup
                        projectId={task.project.id}
                        projectTags={projectTags}
                        setProjectTags={setProjectTags}
                        task={task}
                        getListing={getListing}
                        onChangeTags={onChangeTags}
                      />

                      {task.image?.length > 0 && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.1}
                          onClick={() => onOpenDetail(task)}
                          className="pointer"
                        >
                          <Iconify
                            sx={{ height: 17, width: 17, color: icon_color }}
                            icon="mi:attachment"
                          />
                          <Typography variant="body2">
                            {task.image.length}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>

                    {/* Team & Time Metrics (Mobile) */}
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Stack direction="row">
                        {team.length > 0 && (
                          <TeamMembers
                            disabled={completed}
                            allowOpen
                            taskTeam={team}
                            projectTeam={taskProjectTeam}
                            onChange={onTeamChange}
                            task={task}
                          />
                        )}
                        <TeamPicker
                          onChange={onTeamChange}
                          allowOpen={!completed}
                          taskTeam={team}
                          projectTeam={taskProjectTeam}
                          onOpen={getTaskProjectTeam}
                          loading={loadingTeam}
                        />
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        {time_track.estimated_time && (
                          <Label variant="ghost" color="info">
                            Estimate: {time_track.estimated_time}
                          </Label>
                        )}
                        {task_status == 3 && time_track.actual_time && (
                          <Label variant="ghost" color="info">
                            Completed: {time_track.actual_time}
                          </Label>
                        )}
                        {["admin", "manager"].includes(role) &&
                          time_track.final_time && (
                            <Label variant="ghost" color="info">
                              Billable: {time_track.final_time}
                            </Label>
                          )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Grid>

                {/* Desktop Grid (md and above) */}
                <Grid
                  item
                  md={2}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                  }}
                >
                  <TaskDatePick
                    hasPortal
                    value={end_date}
                    onChange={onDateChange}
                    task={task}
                    disabled={!allowDateAction()}
                  />
                </Grid>
                <Grid
                  item
                  md={5.5}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.7}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Tooltip
                      title={`created by ${creator_profile.first_name} ${creator_profile.last_name}`}
                    >
                      <Box>
                        <UserAvatar
                          sx={{ width: 35, height: 35 }}
                          image={creator_profile.image}
                        />
                      </Box>
                    </Tooltip>
                    <TaskCreatedAt date={createdAt} />
                    <PriorityPopover
                      onChange={onPriorityChange}
                      allowOpen
                      value={priority}
                    />
                    <StatusPopover
                      onChange={onStatusChange}
                      extraStatus={extra_status}
                      allowOpen
                      excludedOptions={excludedStatusOptions()}
                      value={task_status}
                    />
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.1}
                      // Dont open comment drawer instead open task detail & scroll to comment
                      // onClick={() => onOpenComments(task)}
                        onClick={(e) => {
                          onOpenDetail(task);
                          setTimeout(() => {
                            const commentsSection = document.querySelector('[data-comments-section]');
                            if (commentsSection) {
                              commentsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }, 300);
                        }}
                        // -------------------------------------------------
                      className="pointer"
                    >
                      <Badge
                        badgeContent=""
                        color="error"
                        invisible={task.task_comment_unread_count == 0}
                        variant="dot"
                      >
                        <Iconify
                          sx={{ height: 17, width: 17, color: icon_color }}
                          icon="majesticons:comment-text"
                        />
                      </Badge>
                      <Typography variant="body2">
                        {task.task_comment.length}
                      </Typography>
                    </Stack>
                    {task.task_description && (
                      <Iconify
                        sx={{ height: 17, width: 17, color: icon_color }}
                        icon="fluent:text-description-32-filled"
                        onClick={() => onOpenDetail(task)}
                        className="pointer"
                      />
                    )}
                    {task.check_list?.length > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.1}
                        onClick={() => onOpenDetail(task)}
                        className="pointer"
                      >
                        <Iconify
                          sx={{ height: 17, width: 17, color: icon_color }}
                          icon="tabler:checkbox"
                        />
                        <Typography variant="body2">
                          {task.check_list.length}
                        </Typography>
                      </Stack>
                    )}
                    <Iconify
                      sx={{ height: 17, width: 17, color: icon_color }}
                      icon="clarity:copy-line"
                      onClick={() => handleCopy(task)}
                      className="pointer"
                    />
                    <LabelPopup
                      projectId={task.project.id}
                      projectTags={projectTags}
                      setProjectTags={setProjectTags}
                      task={task}
                      getListing={getListing}
                      onChangeTags={onChangeTags}
                    />

                    {task.image?.length > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.1}
                        onClick={() => onOpenDetail(task)}
                        className="pointer"
                      >
                        <Iconify
                          sx={{ height: 17, width: 17, color: icon_color }}
                          icon="mi:attachment"
                        />
                        <Typography variant="body2">
                          {task.image.length}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid
                  item
                  md={4.5}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%", pl: "30px" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Stack direction="row">
                      {team.length > 0 && (
                        <TeamMembers
                          disabled={completed}
                          allowOpen
                          taskTeam={team}
                          projectTeam={taskProjectTeam}
                          onChange={onTeamChange}
                          task={task}

                        />
                      )}
                      <TeamPicker
                        onChange={onTeamChange}
                        allowOpen={!completed}
                        taskTeam={team}
                        projectTeam={taskProjectTeam}
                        onOpen={getTaskProjectTeam}
                        loading={loadingTeam}
                      />
                    </Stack>
                    <Stack alignItems="flex-end" spacing={0.5}>
                      {time_track.estimated_time && (
                        <Label variant="ghost" color="info">
                          Estimate: {time_track.estimated_time}
                        </Label>
                      )}
                      {task_status == 3 && time_track.actual_time && (
                        <Label variant="ghost" color="info">
                          Completed: {time_track.actual_time}
                        </Label>
                      )}
                      {["admin", "manager"].includes(role) &&
                        time_track.final_time && (
                          <Label variant="ghost" color="info">
                            Billable: {time_track.final_time}
                          </Label>
                        )}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Stack>

        {/* Tags */}
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            width: "100%",
            "&::-webkit-scrollbar": {
              height: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: (theme) => theme.palette.divider,
              borderRadius: "10px",
            },
            mt: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{
              width: { xs: "max-content", md: "100%" },
              padding: { xs: "8px 0px 12px", md: "0px" },
              flexWrap: { xs: "nowrap", md: "wrap" },
            }}
          >
            {tag_colors.map((tag) => (
              <Tooltip title={tag.name || ""} key={tag._id}>
                <Box
                  onClick={() => setTagExpand(!tagExpand)}
                  sx={{
                    display: "flex",
                    padding: "3px 5px",
                    borderRadius: "4px",
                    minWidth: "50px",
                    cursor: "pointer",
                    backgroundColor: tagExpand ? "#f5f5f5" : tag.color_code,
                    maxWidth: tagExpand ? "max-content" : "100px",
                    maxHeight: tagExpand ? "20px" : "7px",
                    fontSize: "10px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    borderLeft: tagExpand
                      ? `4px solid ${tag.color_code}`
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                >
                  {tagExpand && tag.name}
                </Box>
              </Tooltip>
            ))}
          </Stack>
        </Box>

        {/* Footer */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          sx={{ px: 1, mt: 1 }}
        >
          <Box className="working-on-task">
            {task?.time_tracking?.map(
              (time_block) =>
                time_block.tracking_status === "start" && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    key={time_block.assignee.user_id}
                    spacing={1}
                  >
                    <Iconify
                      icon="material-symbols:circle"
                      color="#3366FF"
                      fontSize="10px"
                    />
                    <Typography sx={{ fontSize: "10px", color: "#637381" }}>
                      <span style={{ fontWeight: 600 }}>
                        {time_block.assignee.user_id === userProfile.user_id
                          ? "You're"
                          : `${time_block.assignee.first_name} ${time_block.assignee.last_name}`}
                      </span>{" "}
                      currently working
                    </Typography>
                  </Stack>
                )
            )}
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ mt: { xs: 1, md: 0 } }}
          >
            <Typography className="fs-10 fw-5">
              {string_with_limited_length(task?.workspace?.name, 35)}
            </Typography>
            <Iconify icon="grommet-icons:next" sx={{ fontSize: "10px" }} />
            <Typography
              className="fs-10 fw-5 pointer bread-crumb-all-tasks"
              onClick={() => handleNavProject(task)}
            >
              {string_with_limited_length(capitalCase(task?.project?.name), 35)}
            </Typography>
          </Stack>
        </Stack>

        {/* Action Menu */}
        {(["admin", "manager"].includes(role) ||
          task.creator_profile.user_id === userProfile.user_id) && (
          <Box
            sx={{ position: "absolute", top: "10px", right: "10px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton onClick={(e) => handleOpen(e, task)}>
              <Iconify
                sx={{ color: icon_color }}
                icon="bi:three-dots-vertical"
              />
            </IconButton>
            <Menu anchorEl={open} open={Boolean(open)} onClose={handleClose}>
              <MenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskAction("del_task", task);
                  handleClose();
                }}
              >
                Delete{" "}
                <Iconify
                  sx={{ ml: 1, height: 16, width: 16 }}
                  icon="dashicons:trash"
                />
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Paper>
    </React.Fragment>
  );
};

const AllTasksVariant = ({
  task,
  taskTeam,
  onOpenComments,
  onTaskAction,
  onSetSelectedTask,
  userProfile,
  role,
  projects,
  getListing,
  onChangeTags,
  completed = false,
}) => {
  const optimized = useMemo(
    () => (
      <AllTasksVariantUnOptimized
        task={task}
        taskTeam={taskTeam}
        onOpenComments={onOpenComments}
        onTaskAction={onTaskAction}
        onSetSelectedTask={onSetSelectedTask}
        userProfile={userProfile}
        role={role}
        projects={projects}
        getListing={getListing}
        onChangeTags={onChangeTags}
        completed={completed}
      />
    ),
    [task, taskTeam, userProfile, role]
  );

  return <Suspense fallback={<CircularLoader />}>{optimized}</Suspense>;
};

export default AllTasksVariant;
